import { useNavigate } from 'react-router';
import { ADMIN_FIGURES, adminFiguresKey, showsAdminFigures, withWidget } from '@/domain';
import { useJobOrderCounts } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { strings } from '@/shared/strings';
import { KpiCard, KpiGrid } from '@/ui/data/KpiCard';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import styles from './AdminFigures.module.css';

const t = strings.adminFigures;

/** Which list of Orders a figure opens, as the Orders screen reads it from its address. */
export type AdminFigure = 'active' | 'urgent' | 'overdue' | 'held';

const ADMIN_FIGURE_SEARCH: Record<AdminFigure, string> = {
  active: '?status=active',
  urgent: '?urgent=1',
  overdue: '?due=overdue',
  held: '?status=held',
};

/**
 * The admin's figures over the Production line and the Orders list: the cases
 * still being made, the urgent, the overdue and the ones on hold. The lab's
 * request of 17 October 2026, item 5, with active as item 3 says it: a case in
 * Ready or Logistics is not active.
 *
 * Each figure is counted by the server by the very condition the list it opens
 * is narrowed by, so a figure and its list always agree. Shown to a holder of
 * admin until they take it off, and put back from the same place, kept on
 * their account under the screen's own key: what sits on a person's screen is
 * theirs to remove.
 */
export function AdminFigures({
  screen,
  current = null,
}: {
  screen: 'orders' | 'line';
  /** The figure whose list is on screen, so it reads as pressed. Orders only. */
  current?: AdminFigure | null;
}) {
  const navigate = useNavigate();
  const admin = useSession().data?.permissions.includes('admin') === true;
  const layout = useTableLayout(adminFiguresKey(screen));
  const shown = showsAdminFigures(layout.layout);
  const counts = useJobOrderCounts(admin && shown);

  if (!admin) return null;

  function show(on: boolean) {
    layout.save(withWidget(ADMIN_FIGURES, layout.layout, 'figures', on));
    if (!on) toast(t.takenOff);
  }

  if (!shown) {
    return (
      <p className={styles.off}>
        <Button variant="text" onClick={() => show(true)}>
          {t.showAgain}
        </Button>
      </p>
    );
  }

  const value = (pick: (data: NonNullable<typeof counts.data>) => number) =>
    counts.isError ? null : counts.data === undefined ? undefined : pick(counts.data);
  const open = (figure: AdminFigure) => () => void navigate({ pathname: '/orders', search: ADMIN_FIGURE_SEARCH[figure] });

  return (
    <div className={styles.figures}>
      <KpiGrid label={t.label}>
        <KpiCard label={t.active} value={value((data) => data.active)} pressed={current === 'active'} onPress={open('active')} />
        <KpiCard label={t.urgent} value={value((data) => data.urgent)} pressed={current === 'urgent'} onPress={open('urgent')} />
        <KpiCard
          label={t.overdue}
          value={value((data) => data.overdue)}
          pressed={current === 'overdue'}
          onPress={open('overdue')}
        />
        <KpiCard label={t.held} value={value((data) => data.held)} pressed={current === 'held'} onPress={open('held')} />
      </KpiGrid>
      <div className={styles.foot}>
        <Button variant="text" onClick={() => show(false)}>
          {t.hide}
        </Button>
      </div>
    </div>
  );
}
